import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link,
  Image,
  Code,
  Quote,
  Video,
  Paperclip,
} from "lucide-react";
import { toast } from "react-toastify";
import { uploadFile } from "./supportApi";
import {
  compressImage,
  validateImage,
  base64ToFile,
} from "../../lib/imageCompression";

/**
 * Turn whatever a person pastes into the embed URL the help center accepts, or null.
 *
 * The backend takes only /embed/ and player.vimeo.com forms and rejects a
 * `youtube.com/watch?v=` URL with a 400 — which is exactly the URL an author will paste,
 * because it is what the address bar shows. It is refused rather than rewritten server
 * side because watch pages send X-Frame-Options: SAMEORIGIN, so framed they render a
 * blank box nobody notices until a reader complains.
 */
export function toEmbedUrl(input) {
  try {
    const u = new URL(String(input).trim());
    const asId = (p) => (/^[\w-]+$/.test(p) ? p : null);

    if (/^(www\.)?youtube(-nocookie)?\.com$/.test(u.hostname)) {
      if (u.pathname.startsWith("/embed/")) return u.toString();
      const v = u.searchParams.get("v");
      return v && asId(v) ? `https://www.youtube.com/embed/${v}` : null;
    }
    if (u.hostname === "youtu.be") {
      const v = u.pathname.slice(1);
      return asId(v) ? `https://www.youtube.com/embed/${v}` : null;
    }
    if (u.hostname === "player.vimeo.com") return u.toString();
    if (/^(www\.)?vimeo\.com$/.test(u.hostname)) {
      const v = u.pathname.slice(1);
      return /^\d+$/.test(v) ? `https://player.vimeo.com/video/${v}` : null;
    }
    return null;
  } catch {
    return null;
  }
}

/** Escape a value going into an HTML attribute or text node. */
const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const RichTextEditor = forwardRef(function RichTextEditor(
  { value, onChange, placeholder, className },
  ref,
) {
  const editorRef = useRef(null);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [currentUpload, setCurrentUpload] = useState(null);
  const currentXHRRef = useRef(null);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    getHTML: () => {
      return editorRef.current?.innerHTML || "";
    },
    getEditorRef: () => {
      return editorRef.current;
    },
  }));

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  // Process upload queue
  useEffect(() => {
    if (uploadQueue.length > 0 && !currentUpload) {
      processNextUpload();
    }
  }, [uploadQueue, currentUpload]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();

    // Check for images in clipboard
    const items = e.clipboardData.items;
    let hasImage = false;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        hasImage = true;
        const file = items[i].getAsFile();
        if (file) {
          addToUploadQueue(file);
        }
      }
    }

    // If no image, handle text/HTML paste
    if (!hasImage) {
      const html = e.clipboardData.getData("text/html");
      const text = e.clipboardData.getData("text/plain");

      if (html) {
        // Create a temporary element to parse and clean the HTML
        const temp = document.createElement("div");
        temp.innerHTML = html;

        // Check for base64 images and convert them
        const images = temp.querySelectorAll("img");
        images.forEach((img) => {
          const src = img.getAttribute("src");
          if (src && src.startsWith("data:image")) {
            // Convert base64 to file and add to queue
            try {
              const file = base64ToFile(src, `pasted-image-${Date.now()}.png`);
              addToUploadQueue(file);
              // Remove the base64 image from HTML
              img.remove();
            } catch (error) {
              console.error("Failed to convert base64 image:", error);
            }
          }
        });

        // Remove color and font styles from all elements
        const allElements = temp.querySelectorAll("*");
        allElements.forEach((el) => {
          el.style.color = "";
          el.style.backgroundColor = "";
          el.style.fontFamily = "";
          el.removeAttribute("color");
          el.removeAttribute("face");
        });

        // Insert cleaned HTML
        document.execCommand("insertHTML", false, temp.innerHTML);
      } else {
        // Fallback to plain text
        document.execCommand("insertText", false, text);
      }

      handleInput();
    }
  };

  const addToUploadQueue = (file, caption = "") => {
    // Ensure editor is focused when adding to queue
    if (editorRef.current) {
      editorRef.current.focus();
    }

    const uploadId = `upload-${Date.now()}-${Math.random()}`;

    // Generate alt text from filename (remove extension)
    const altText = file.name.replace(/\.[^/.]+$/, "");

    setUploadQueue((prev) => [
      ...prev,
      {
        id: uploadId,
        file,
        altText, // ✅ Store alt text
        caption, // wraps the image in <figure> when set
        progress: 0,
        error: null,
        retryCount: 0,
      },
    ]);
  };

  const processNextUpload = async () => {
    if (uploadQueue.length === 0) return;

    const upload = uploadQueue[0];
    setCurrentUpload(upload);

    // Insert placeholder in editor
    const placeholderId = `placeholder-${upload.id}`;
    insertPlaceholder(placeholderId, upload.progress);

    try {
      // Validate image
      validateImage(upload.file);

      // Compress image
      const compressedFile = await compressImage(upload.file, (progress) => {
        updatePlaceholderProgress(placeholderId, progress);
      });

      // Upload image
      const response = await uploadFile(
        compressedFile,
        "article",
        (progress) => {
          updatePlaceholderProgress(placeholderId, progress);
        },
      );

      if (response && response.url) {
        // Find placeholder and replace with image directly
        const placeholder = document.getElementById(placeholderId);

        if (placeholder) {
          // Create image element with ONLY allowed attributes
          const img = document.createElement("img");
          img.src = response.url; // ✅ Allowed by backend
          img.alt = upload.altText || ""; // ✅ Allowed by backend
          // ❌ NO style attributes - backend strips them!

          if (upload.caption) {
            // <figure> is what ties the caption to the image. Without it the sanitizer
            // used to drop the wrapper and the caption merged into the next paragraph.
            const figure = document.createElement("figure");
            const figcaption = document.createElement("figcaption");
            figcaption.textContent = upload.caption;
            figure.appendChild(img);
            figure.appendChild(figcaption);
            placeholder.replaceWith(figure);
          } else {
            placeholder.replaceWith(img);
          }

          // Trigger onChange to update parent component
          handleInput();
        }

        // Success toast
        toast.success("Image uploaded successfully");

        // Remove from queue
        setUploadQueue((prev) => prev.slice(1));
        setCurrentUpload(null);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Upload error:", error);

      // Update placeholder with error
      updatePlaceholderError(placeholderId, error.message, upload.id);

      // Update queue with error
      setUploadQueue((prev) =>
        prev.map((item) =>
          item.id === upload.id ? { ...item, error: error.message } : item,
        ),
      );
      setCurrentUpload(null);

      // Show error toast
      toast.error(error.message || "Upload failed. Please try again.");
    }
  };

  const insertPlaceholder = (placeholderId, progress) => {
    if (!editorRef.current) {
      return;
    }

    // Ensure editor is focused first
    editorRef.current.focus();

    // Small delay to ensure focus is applied before inserting
    setTimeout(() => {
      const placeholder = `
        <div id="${placeholderId}" class="upload-placeholder" style="display: inline-block; width: 300px; padding: 12px; background: #232323; border: 1px solid #146b4f; border-radius: 8px; margin: 8px 0;">
          <div class="progress-bar" style="width: 100%; background: #171717; border-radius: 4px; height: 8px; overflow: hidden;">
            <div class="progress-fill" style="width: ${progress}%; background: #10b981; height: 100%; transition: width 0.3s;"></div>
          </div>
          <p style="color: #9ca3af; font-size: 12px; margin-top: 8px;">${progress}% uploaded</p>
        </div>
      `;

      document.execCommand("insertHTML", false, placeholder);

      handleInput();
    }, 10);
  };

  const updatePlaceholderProgress = (placeholderId, progress) => {
    const placeholder = document.getElementById(placeholderId);
    if (placeholder) {
      const progressFill = placeholder.querySelector(".progress-fill");
      const progressText = placeholder.querySelector("p");
      if (progressFill) {
        progressFill.style.width = `${progress}%`;
      }
      if (progressText) {
        progressText.textContent = `${progress}% uploaded`;
      }
    }
  };

  const updatePlaceholderError = (placeholderId, errorMessage, uploadId) => {
    const placeholder = document.getElementById(placeholderId);
    if (placeholder) {
      placeholder.innerHTML = `
        <p style="color: #ef4444; font-size: 12px; margin-bottom: 8px;">${errorMessage}</p>
        <div style="display: flex; gap: 8px;">
          <button 
            onclick="window.retryUpload('${uploadId}')" 
            style="flex: 1; padding: 6px 12px; background: #10b981; color: white; border: none; border-radius: 6px; font-size: 12px; cursor: pointer;"
          >
            Retry Upload
          </button>
          <button 
            onclick="window.cancelUpload('${placeholderId}')" 
            style="flex: 1; padding: 6px 12px; background: #6b7280; color: white; border: none; border-radius: 6px; font-size: 12px; cursor: pointer;"
          >
            Cancel
          </button>
        </div>
      `;
    }
  };

  const removePlaceholder = (placeholderId) => {
    const placeholder = document.getElementById(placeholderId);
    if (placeholder) {
      placeholder.remove();
      handleInput();
    }
  };

  const retryUpload = (uploadId) => {
    const upload = uploadQueue.find((u) => u.id === uploadId);
    if (upload && upload.retryCount < 2) {
      // Remove error state
      setUploadQueue((prev) =>
        prev.map((item) =>
          item.id === uploadId
            ? { ...item, error: null, retryCount: item.retryCount + 1 }
            : item,
        ),
      );
      setCurrentUpload(null);

      // Remove error placeholder
      const placeholders = document.querySelectorAll(".upload-placeholder");
      placeholders.forEach((p) => {
        if (p.innerHTML.includes(uploadId)) {
          p.remove();
        }
      });
      handleInput();
    } else {
      toast.error("Maximum retry attempts reached");
      cancelUploadById(uploadId);
    }
  };

  const cancelUploadById = (uploadId) => {
    // Remove from queue
    setUploadQueue((prev) => prev.filter((item) => item.id !== uploadId));

    // Remove placeholder
    const placeholders = document.querySelectorAll(".upload-placeholder");
    placeholders.forEach((p) => {
      if (p.id.includes(uploadId) || p.innerHTML.includes(uploadId)) {
        p.remove();
      }
    });

    setCurrentUpload(null);
    handleInput();
    toast.info("Upload cancelled");
  };

  const cancelPlaceholder = (placeholderId) => {
    removePlaceholder(placeholderId);

    // Find and remove from queue
    const upload = uploadQueue.find(
      (u) => `placeholder-${u.id}` === placeholderId,
    );
    if (upload) {
      cancelUploadById(upload.id);
    }
  };

  // Expose functions to window for onclick handlers
  useEffect(() => {
    window.retryUpload = retryUpload;
    window.cancelUpload = cancelPlaceholder;

    return () => {
      delete window.retryUpload;
      delete window.cancelUpload;
    };
  }, [uploadQueue]);

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const insertHTML = (html) => {
    document.execCommand("insertHTML", false, html);
    editorRef.current?.focus();
  };

  const handleLink = () => {
    const url = prompt("Enter URL:");
    if (url) {
      execCommand("createLink", url);
    }
  };

  const handleImageButton = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg,image/png,image/gif,image/webp";
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      // Only the button path asks — pasting a screenshot is the common way images get in,
      // and a prompt on every paste would be in the way.
      const caption = prompt("Caption (optional):");
      addToUploadQueue(file, caption?.trim() || "");
    };
    input.click();
  };

  /**
   * A bad embed is the one thing the backend rejects loudly — it answers 400 and the whole
   * save fails. Catching it here means the author loses a prompt, not their article.
   */
  const handleVideoButton = () => {
    const raw = prompt("Video URL (YouTube or Vimeo):");
    if (!raw) return;

    const url = toEmbedUrl(raw);
    if (!url) {
      toast.error(
        "That is not a YouTube or Vimeo video URL. Copy the link from the video's page.",
      );
      return;
    }

    insertHTML(
      `<iframe src="${escapeHtml(url)}" width="560" height="315" title="Video" ` +
        `allowfullscreen frameborder="0"></iframe><p><br></p>`,
    );
    handleInput();
  };

  const handleFileButton = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/pdf";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const toastId = toast.loading("Uploading PDF...");
      try {
        // Straight to the endpoint — compressImage only understands images, and a PDF must
        // reach the server byte for byte.
        const response = await uploadFile(file, "article");
        const label = `${response.filename || file.name} (PDF${
          response.size ? `, ${formatBytes(response.size)}` : ""
        })`;

        // No `rel`: the sanitizer stamps rel="noopener noreferrer" onto every
        // target="_blank" link, so whatever we send is replaced anyway.
        insertHTML(
          `<a href="${escapeHtml(response.url)}" ` +
            `download="${escapeHtml(response.filename || file.name)}" ` +
            `target="_blank">${escapeHtml(label)}</a>&nbsp;`,
        );
        handleInput();
        toast.update(toastId, {
          render: "PDF attached",
          type: "success",
          isLoading: false,
          autoClose: 2500,
        });
      } catch (error) {
        toast.update(toastId, {
          render: error.message || "Could not upload the PDF",
          type: "error",
          isLoading: false,
          autoClose: 4000,
        });
      }
    };
    input.click();
  };

  const buttons = [
    { icon: Bold, command: "bold", title: "Bold" },
    { icon: Italic, command: "italic", title: "Italic" },
    { icon: Underline, command: "underline", title: "Underline" },
    { icon: List, command: "insertUnorderedList", title: "Bullet List" },
    { icon: ListOrdered, command: "insertOrderedList", title: "Numbered List" },
    {
      icon: Quote,
      command: "formatBlock",
      value: "blockquote",
      title: "Quote",
    },
    { icon: Code, command: "formatBlock", value: "pre", title: "Code Block" },
    { icon: Link, onClick: handleLink, title: "Insert Link" },
    { icon: Image, onClick: handleImageButton, title: "Insert Image" },
    { icon: Video, onClick: handleVideoButton, title: "Embed Video" },
    { icon: Paperclip, onClick: handleFileButton, title: "Attach PDF" },
  ];

  return (
    <>
      <div
        className={`border border-[#146b4f] rounded-lg bg-[#232323] ${className}`}
      >
        {/* Toolbar */}
        <div className="flex flex-wrap gap-1 p-2 border-b border-[#146b4f]">
          <select
            onChange={(e) => execCommand("formatBlock", e.target.value)}
            className="px-2 py-1 bg-[#171717] text-white border border-[#146b4f] rounded text-xs"
            style={{ fontFamily: '"Scarlex Regular", sans-serif' }}
          >
            <option value="p">Normal</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
          </select>

          {buttons.map((btn, index) => {
            const Icon = btn.icon;
            return (
              <button
                key={index}
                type="button"
                onClick={() =>
                  btn.onClick
                    ? btn.onClick()
                    : execCommand(btn.command, btn.value)
                }
                className="p-2 hover:bg-[#146b4f] rounded transition-colors"
                title={btn.title}
              >
                <Icon className="h-4 w-4 text-white" />
              </button>
            );
          })}
        </div>

        {/* Editor */}
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onPaste={handlePaste}
          className="min-h-[300px] p-4 text-white outline-none prose prose-invert max-w-none"
          style={{ fontFamily: '"Scarlex Regular", sans-serif' }}
          data-placeholder={placeholder}
        />

        <style>{`
          [contenteditable]:empty:before {
            content: attr(data-placeholder);
            color: #6b7280;
            pointer-events: none;
          }
          [contenteditable] h1 {
            font-size: 2em;
            font-weight: bold;
            margin: 0.5em 0;
          }
          [contenteditable] h2 {
            font-size: 1.5em;
            font-weight: bold;
            margin: 0.5em 0;
          }
          [contenteditable] h3 {
            font-size: 1.25em;
            font-weight: bold;
            margin: 0.5em 0;
          }
          [contenteditable] ul,
          [contenteditable] ol {
            margin: 0.5em 0;
            padding-left: 2em;
          }
          [contenteditable] blockquote {
            border-left: 4px solid #146b4f;
            padding-left: 1em;
            margin: 0.5em 0;
            color: #9ca3af;
          }
          [contenteditable] pre {
            background: #171717;
            padding: 1em;
            border-radius: 0.5em;
            overflow-x: auto;
            margin: 0.5em 0;
          }
          [contenteditable] a {
            color: #10b981;
            text-decoration: underline;
          }
          [contenteditable] img {
            max-width: 100%;
            height: auto;
            margin: 0.5em 0;
            border-radius: 0.5em;
          }
        `}</style>
      </div>
    </>
  );
});
